import type { VectorizeResult, VectorizeSettings } from './engine';
import { isTraceProgress, type TraceProgress } from './trace-progress';

export type { TraceProgress, TraceStage } from './trace-progress';

export type VectorSource = { file: File; url: string; name: string; width: number; height: number };
/** 0 means the exact source dimensions; other values explicitly choose a smaller working raster. */
export type TraceResolution = 0 | 512 | 768 | 1024;

/** Native tracing never silently discards source pixels. Explicit speed modes never upscale. */
export function tracingDimensions(width: number, height: number, resolution: TraceResolution) {
  if (resolution === 0) return { width, height };
  const scale = Math.min(1, resolution / Math.max(width, height));
  return { width: Math.max(1, Math.floor(width * scale)), height: Math.max(1, Math.floor(height * scale)) };
}

export async function inspectVectorSource(file: File): Promise<VectorSource> {
  if (!/\.(png|jpe?g|webp|bmp)$/i.test(file.name)) throw new Error('format');
  if (file.size > 20 * 1024 * 1024) throw new Error('file-size');
  let bitmap: ImageBitmap;
  try { bitmap = await createImageBitmap(file); } catch { throw new Error('decode'); }
  try {
    if (!bitmap.width || !bitmap.height) throw new Error('decode');
    return { file, url: URL.createObjectURL(file), name: file.name, width: bitmap.width, height: bitmap.height };
  } finally { bitmap.close(); }
}

/** Only the worker imports the tracing engine at runtime. File pixels never leave the device. */
export async function traceVectorSource(source: VectorSource, settings: VectorizeSettings, resolution: TraceResolution, signal: AbortSignal, progress: (value: TraceProgress) => void): Promise<VectorizeResult> {
  const abort = () => new DOMException('Cancelled', 'AbortError');
  if (signal.aborted) throw abort();
  progress({ stage: 'decoding' });
  const bitmap = await createImageBitmap(source.file);
  let canvas: HTMLCanvasElement | null = null;
  try {
    if (signal.aborted) throw abort();
    const dimensions = tracingDimensions(bitmap.width, bitmap.height, resolution);
    // Only the legacy pixel contour engine has this fixed processing budget.
    // Refuse explicitly rather than quietly lowering native source fidelity.
    if (settings.smoothing === 0 && dimensions.width * dimensions.height > 1_500_000) throw new Error('pixel-engine-budget');
    canvas = document.createElement('canvas');
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('canvas');
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
    if (signal.aborted) throw abort();
    progress({ stage: 'tracing' });
    const worker = new Worker('/assets/vectorize-worker.js');
    try {
      const result = await new Promise<VectorizeResult>((resolve, reject) => {
        const cleanup = () => { signal.removeEventListener('abort', cancel); worker.onmessage = null; worker.onerror = null; worker.onmessageerror = null; };
        const cancel = () => { cleanup(); reject(abort()); };
        signal.addEventListener('abort', cancel, { once: true });
        worker.onerror = () => { cleanup(); reject(new Error('worker')); };
        worker.onmessageerror = () => { cleanup(); reject(new Error('worker')); };
        worker.onmessage = ({ data }: MessageEvent<{ result?: VectorizeResult; error?: string; progress?: unknown }>) => {
          if (data.progress !== undefined) {
            if (isTraceProgress(data.progress)) progress(data.progress);
            return;
          }
          cleanup();
          if (data.result) resolve(data.result);
          else reject(new Error(data.error || 'trace'));
        };
        if (signal.aborted) { cancel(); return; }
        try { worker.postMessage({ width: pixels.width, height: pixels.height, data: pixels.data, settings }, [pixels.data.buffer]); }
        catch { cleanup(); reject(new Error('worker')); }
      });
      // Preserve source document dimensions while paths retain their working-resolution viewBox.
      const document = new DOMParser().parseFromString(result.svg, 'image/svg+xml');
      if (document.querySelector('parsererror')) throw new Error('trace');
      document.documentElement.setAttribute('width', String(source.width));
      document.documentElement.setAttribute('height', String(source.height));
      return { ...result, svg: new XMLSerializer().serializeToString(document) };
    } finally { worker.terminate(); }
  } finally { bitmap.close(); if (canvas) canvas.width = canvas.height = 0; }
}

/** A labeled raster example, so the complete import → trace → SVG flow is available immediately. */
export async function createVectorSample(): Promise<File> {
  const canvas = document.createElement('canvas');
  canvas.width = 800; canvas.height = 600;
  try {
    const context = canvas.getContext('2d');
    if (!context) throw new Error('canvas');
    context.fillStyle = '#f5f0e8'; context.fillRect(0, 0, 800, 600);
    context.fillStyle = '#292336';
    context.beginPath(); context.moveTo(150, 450); context.lineTo(355, 105); context.quadraticCurveTo(400, 40, 445, 105); context.lineTo(650, 450); context.quadraticCurveTo(680, 510, 610, 510); context.lineTo(190, 510); context.quadraticCurveTo(120, 510, 150, 450); context.fill();
    context.fillStyle = '#f5f0e8'; context.beginPath(); context.moveTo(325, 350); context.lineTo(400, 220); context.lineTo(475, 350); context.closePath(); context.fill();
    context.fillStyle = '#955de6'; context.beginPath(); context.ellipse(395, 390, 240, 56, -0.32, 0, Math.PI * 2); context.fill();
    context.fillStyle = '#eeb050'; context.beginPath(); context.arc(590, 145, 54, 0, Math.PI * 2); context.fill();
    const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error('canvas')), 'image/png'));
    return new File([blob], 'designforge-sample.png', { type: 'image/png' });
  } finally { canvas.width = canvas.height = 0; }
}

export function downloadVector(svg: string, name: string) {
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = `${name.replace(/\.[^.]+$/, '') || 'image'}-paths.svg`;
  document.body.append(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return blob.size;
}
